import { DateField } from "@fawkes/ui";
import { useEffect, useRef, useState } from "react";
import {
  Building2,
  CakeSlice,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  Layers,
  ListFilter,
  MapPin,
  PawPrint,
  RotateCcw,
  Split,
} from "lucide-react";

import { WOONPLAATS_VOORVOEGSEL, type Gebied } from "../api/insights";

/**
 * De filterbalk: welk cijfer, en waarover.
 *
 * <p>Elk filter heeft zijn label erboven en zijn <b>waarde</b> in het veld ("2024 t/m 2026", "Hond,
 * Kat"). Dat is niet alleen opmaak — zo lees je de afbakening af zonder de velden langs te gaan.
 * Juist bij een cijfer dat in een verslag belandt is dat het verschil tussen "ik weet waar dit over
 * gaat" en "ik neem aan van wel".
 *
 * <p><b>De filters worden pas toegepast als je op Filter klikt.</b> Bij elke wijziging opnieuw
 * rekenen lijkt vlotter en is het niet: wie van 2020 naar 2025 gaat, gaat langs 202, 2025 en 20250 —
 * drie zware analyses die niemand gevraagd heeft, en het scherm springt onderwijl heen en weer.
 */

export interface Onderdeel {
  id: string;
  familie: string;
  naam: string;
  /** Wat er precies geteld wordt. Hoort bij het antwoord te staan, niet in de documentatie. */
  uitleg: string;
  /** Onderdelen zonder periode gaan over "nu"; dan heeft het bereik geen betekenis. */
  heeftPeriode: boolean;
  /** Wat er gerekend wordt. Bij een gemiddelde valt er niets cumulatief op te tellen. */
  maat?: string;
}

/** De eenheid van een leeftijdsgrens. Weken voor pups en kittens, jaren voor de rest. */
export type Leeftijdseenheid = "WEKEN" | "MAANDEN" | "JAREN";

export interface FilterKeuze {
  onderdeel: string;
  /** Het bereik in jaartallen; wordt gebruikt zolang de as per jaar loopt. */
  vanJaar: number | null;
  totJaar: number | null;
  /**
   * Het bereik op de dag af.
   *
   * <p>Als ISO-datum en niet als Date: een Date krijgt een tijd en een tijdzone mee die hier niets
   * betekenen, en dan schuift "1 januari" op sommige machines een dag op.
   */
  vanDatum: string | null;
  totDatum: string | null;
  /**
   * De gekozen diersoorten; leeg is alle.
   *
   * <p>Een lijst en niet één keuze: "honden en katten samen, maar niet de knaagdieren" is een
   * normale vraag, en met één keuze moet je die in twee analyses knippen en de uitkomsten met de
   * hand optellen.
   */
  diersoorten: string[];
  uitsplitsing: string;
  /** Een gemeente, of een woonplaats met {@link WOONPLAATS_VOORVOEGSEL} ervoor. Leeg is alle. */
  gemeente: string;
  /** Alleen dieren ouder dan dit; leeg laat de ondergrens los. */
  ouderDan: number | null;
  ouderDanEenheid: Leeftijdseenheid;
  /** Alleen dieren jonger dan dit; leeg laat de bovengrens los. */
  jongerDan: number | null;
  jongerDanEenheid: Leeftijdseenheid;
  /**
   * De aangevinkte vestigingen.
   *
   * <p>Leeg betekent de vestiging waar je in zit — niet "alle". Dat onderscheid doet ertoe: "alle"
   * zou suggereren dat je overal in mag kijken, en dat bepaalt de server en niet dit scherm.
   */
  vestigingen: string[];
  /** De eenheid van de periode-as. */
  eenheid: Eenheid;
  /**
   * Oplopend totaal in plaats van per periode.
   *
   * <p>Staat in de keuze maar niet in de filterbalk: het is een andere manier om dezelfde cijfers
   * te tonen en geen andere vraag, dus de schakelaar hoort bij de grafiek. Zou de server het doen,
   * dan moest er voor het omzetten opnieuw gerekend worden.
   */
  cumulatief: boolean;
}

/**
 * De eenheid van de periode-as, van grof naar fijn.
 *
 * <p>Dezelfde vijf als het oude statistiekenscherm. Let op wat "week" betekent: een week binnen
 * het eigen kalenderjaar, dus week 1 en week 53 zijn korter dan zeven dagen. Dat is met opzet — bij
 * ISO-weken hoort 29 december 2025 bij week 1 van 2026, en dan komt die dag in een groep te staan
 * die "2025-W01" heet en niet bestaat.
 */
export type Eenheid = "DAG" | "WEEK" | "MAAND" | "KWARTAAL" | "JAAR";

/** De eenheden met hun tekst, van grof naar fijn zoals in het oude scherm. */
const EENHEDEN: { sleutel: Eenheid; label: string }[] = [
  { sleutel: "JAAR", label: "dataInsight.perJaar" },
  { sleutel: "KWARTAAL", label: "dataInsight.perKwartaal" },
  { sleutel: "MAAND", label: "dataInsight.perMaand" },
  { sleutel: "WEEK", label: "dataInsight.perWeek" },
  { sleutel: "DAG", label: "dataInsight.perDag" },
];

/** De diersoorten uit het schema. Dezelfde lijst als waar de server op filtert. */
export const DIERSOORTEN = ["Hond", "Kat", "Konijn", "Cavia", "Hamster", "Muis", "Rat", "Vogel"];

/** De eenheden van een leeftijdsgrens, met hun tekst. */
const LEEFTIJDSEENHEDEN: { sleutel: Leeftijdseenheid; label: string }[] = [
  { sleutel: "WEKEN", label: "dataInsight.leeftijdWeken" },
  { sleutel: "MAANDEN", label: "dataInsight.leeftijdMaanden" },
  { sleutel: "JAREN", label: "dataInsight.leeftijdJaren" },
];

/**
 * Waarop je een onderdeel kunt uitsplitsen.
 *
 * <p>De namen komen uit het scherm dat dit vervangt: "Huidige vestiging" is daar de stand zonder
 * uitsplitsing — één reeks, voor de vestiging waar je op dat moment in zit. Regio's en Landelijk
 * staan er niet bij: die kent deze database niet, en een keuze die stilzwijgend hetzelfde antwoord
 * geeft als "huidige vestiging" is erger dan een keuze die er niet is.
 */
const GROEPEN = [
  { pad: "", sleutel: "dataInsight.groepHuidige" },
  { pad: "animal.opnameVestiging", sleutel: "dataInsight.groepVestigingen" },
  { pad: "animal.species", sleutel: "dataInsight.groepDiersoort" },
  { pad: "animal.gemeente", sleutel: "dataInsight.groepGemeente" },
];

/** Een datum als ISO-dag, zonder tijd en zonder tijdzone. */
function iso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dag = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${dag}`;
}

/** Wat een snelkeuze aan de stand verandert. */
export interface PeriodeStand {
  vanJaar: number | null;
  totJaar: number | null;
  vanDatum: string | null;
  totDatum: string | null;
  eenheid: Eenheid;
}

/**
 * De snelkeuzes voor de periode.
 *
 * <p>Ze zetten óók de eenheid, en dat is de hele bedoeling: "deze maand" met een as per jaar levert
 * één staaf op, en "de laatste vijf jaar" per dag levert achttienhonderd staven. De combinatie is
 * wat iemand bedoelt, niet het bereik alleen.
 *
 * <p>De peildatum is een parameter zodat een test hem kan vastzetten; zonder dat zou elke test die
 * "dit jaar" nagaat op 1 januari iets anders vinden dan op 31 december.
 */
export function periodeSnelkeuzes(vandaag: Date = new Date()):
{ sleutel: string; label: string; stand: PeriodeStand }[] {
  const jaar = vandaag.getFullYear();
  const kwartaalStart = new Date(jaar, Math.floor(vandaag.getMonth() / 3) * 3, 1);
  const twaalf = new Date(jaar, vandaag.getMonth() - 11, 1);
  return [
    {
      sleutel: "dezeMaand",
      label: "dataInsight.periodeDezeMaand",
      // Per dag: binnen één maand is een maandstaaf één staaf.
      stand: { vanJaar: null, totJaar: null, eenheid: "DAG",
        vanDatum: iso(new Date(jaar, vandaag.getMonth(), 1)), totDatum: iso(vandaag) },
    },
    {
      sleutel: "ditKwartaal",
      label: "dataInsight.periodeDitKwartaal",
      stand: { vanJaar: null, totJaar: null, eenheid: "WEEK",
        vanDatum: iso(kwartaalStart), totDatum: iso(vandaag) },
    },
    {
      sleutel: "ditJaar",
      label: "dataInsight.periodeDitJaar",
      stand: { vanJaar: null, totJaar: null, eenheid: "MAAND",
        vanDatum: iso(new Date(jaar, 0, 1)), totDatum: iso(vandaag) },
    },
    {
      sleutel: "laatste12",
      label: "dataInsight.periodeLaatste12",
      stand: { vanJaar: null, totJaar: null, eenheid: "MAAND",
        vanDatum: iso(twaalf), totDatum: iso(vandaag) },
    },
    {
      sleutel: "vorigJaar",
      label: "dataInsight.periodeVorigJaar",
      stand: { vanJaar: null, totJaar: null, eenheid: "MAAND",
        vanDatum: iso(new Date(jaar - 1, 0, 1)), totDatum: iso(new Date(jaar - 1, 11, 31)) },
    },
    {
      sleutel: "vijfJaar",
      label: "dataInsight.periodeVijfJaar",
      // In jaartallen en niet in datums: bij een as per jaar is de dag betekenisloos, en een
      // jaartal is wat iemand hier intikt.
      stand: { vanJaar: jaar - 4, totJaar: jaar, vanDatum: null, totDatum: null, eenheid: "JAAR" },
    },
  ];
}

/**
 * Dezelfde periode, uitgedrukt in de eenheid die erbij past.
 *
 * <p>Per jaar zijn jaartallen genoeg en is een dag betekenisloos; fijner dan een jaar heeft een
 * jaartal juist te weinig houvast — "2025" als je per week kijkt zegt niet waar je wil beginnen.
 * Daarom wordt het bereik meegenomen in plaats van gewist: wie van jaar naar maand omschakelt is
 * niet van periode veranderd, alleen van detail.
 */
export function bijEenheid(k: FilterKeuze, nieuw: Eenheid): Partial<FilterKeuze> {
  if (nieuw === "JAAR") {
    return {
      eenheid: nieuw,
      vanJaar: k.vanJaar ?? (k.vanDatum ? Number(k.vanDatum.slice(0, 4)) : null),
      totJaar: k.totJaar ?? (k.totDatum ? Number(k.totDatum.slice(0, 4)) : null),
      vanDatum: null,
      totDatum: null,
    };
  }
  return {
    eenheid: nieuw,
    vanDatum: k.vanDatum ?? (k.vanJaar ? `${k.vanJaar}-01-01` : null),
    totDatum: k.totDatum ?? (k.totJaar ? `${k.totJaar}-12-31` : null),
  };
}

/**
 * Eén filter: het label erboven, het veld eronder.
 *
 * <p>Het label boven en niet ervoor, zodat de waarde de nadruk krijgt. "Diersoort: Alle
 * diersoorten" leest als een instelling; met het label erboven lees je "Alle diersoorten" en zie je
 * pas daarna waar dat over gaat — en dat is de volgorde waarin je een filterbalk scant.
 */
export function Filter({ icoon, naam, kind, breed, kleur }: {
  icoon: React.ReactNode;
  naam: string;
  kind: React.ReactNode;
  breed?: boolean;
  /**
   * Welk accent de pil krijgt.
   *
   * <p>Niet als versiering: de kleur is per filter vast, dus je vindt "diersoort" terug aan zijn
   * kleur voordat je het label gelezen hebt. Dat werkt alleen zolang de kleuren niet verschuiven,
   * en daarom staan ze hier bij het filter en niet bij de plek in de rij.
   */
  kleur?: string;
}) {
  return (
    <div className={"fawkes-di-filter" + (breed ? " fawkes-di-filter--breed" : "")}>
      <span className="fawkes-di-filter__label">{naam}</span>
      <div className={"fawkes-di-pil" + (kleur ? ` fawkes-di-pil--${kleur}` : "")}>
        <span className="fawkes-di-pil__icoon" aria-hidden="true">{icoon}</span>
        {kind}
      </div>
    </div>
  );
}

/** Een keuzelijst binnen een pil: geen eigen rand, met een chevron erachter. */
export function PilKeuze({ waarde, aria, onKies, kinderen }: {
  waarde: string;
  aria: string;
  onKies: (v: string) => void;
  kinderen: React.ReactNode;
}) {
  return (
    <span className="fawkes-di-pil__keuzehouder">
      <select
        className="fawkes-di-pil__keuze"
        value={waarde}
        aria-label={aria}
        onChange={(e) => onKies(e.target.value)}
      >
        {kinderen}
      </select>
      <ChevronDown className="fawkes-di-pil__chevron" aria-hidden="true" />
    </span>
  );
}

/**
 * Klapt een uitklappil in zodra er ernaast wordt geklikt, of op Escape.
 *
 * <p>Een {@code details} blijft uit zichzelf open tot je nog eens op de pil zelf klikt. Dat is niet hoe
 * een keuzelijst werkt: wie ernaast klikt is klaar met kiezen, en een open lijst die over de kaart of de
 * volgende pil heen blijft hangen leest als een scherm dat vastzit. Luisteren in de opvangfase, zodat
 * ook een klik op de kaart (die zijn eigen muisafhandeling heeft) de pil sluit.
 */
export function useInklappenErnaast() {
  const ref = useRef<HTMLDetailsElement | null>(null);
  useEffect(() => {
    const klik = (e: Event) => {
      const pil = ref.current;
      if (pil?.open && e.target instanceof Node && !pil.contains(e.target)) pil.open = false;
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape" && ref.current?.open) ref.current.open = false;
    };
    document.addEventListener("pointerdown", klik, true);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("pointerdown", klik, true);
      document.removeEventListener("keydown", toets);
    };
  }, []);
  return ref;
}

/**
 * Een pil die openklapt naar aanvinkhokjes, voor een filter met meerdere waarden.
 *
 * <p>Een {@code details} en geen eigen uitklapmechaniek: die sluit op Escape, is met het toetsenbord
 * te bedienen en werkt zonder dat er javascript aan te pas komt.
 */
export function PilLijst({ icoon, naam, samenvatting, kleur, opties, gekozen, onZet, alle }: {
  /**
   * De tekst van een hokje bovenaan dat alles in één keer aanvinkt, of niets voor geen zo'n hokje.
   *
   * <p>Bij tweeëntwintig vestigingen is "alle" anders tweeëntwintig klikken. Uitvinken zet de keuze
   * leeg, en leeg betekent wat het scherm ervan maakt (bij vestigingen: de huidige).
   */
  alle?: string;
  icoon: React.ReactNode;
  naam: string;
  /** Wat er dichtgeklapt staat: de gekozen waarden, of wat "niets gekozen" betekent. */
  samenvatting: string;
  kleur?: string;
  opties: string[];
  gekozen: string[];
  onZet: (v: string[]) => void;
}) {
  const inklap = useInklappenErnaast();
  return (
    <div className="fawkes-di-filter">
      <span className="fawkes-di-filter__label">{naam}</span>
      <details ref={inklap} className={"fawkes-di-pil fawkes-di-pil--lijst"
        + (kleur ? ` fawkes-di-pil--${kleur}` : "")}>
        <summary className="fawkes-di-pil__samenvatting">
          <span className="fawkes-di-pil__icoon" aria-hidden="true">{icoon}</span>
          <span className="fawkes-di-pil__waarde">{samenvatting}</span>
          <ChevronDown className="fawkes-di-pil__chevron" aria-hidden="true" />
        </summary>
        <div className="fawkes-di-pil__hokjes">
          {alle && opties.length > 1 ? (
            <label className="fawkes-di-pil__hokje fawkes-di-pil__hokje--alle">
              <input
                type="checkbox"
                checked={opties.every((o) => gekozen.includes(o))}
                onChange={(e) => onZet(e.target.checked ? [...opties] : [])}
              />
              <span>{alle}</span>
            </label>
          ) : null}
          {opties.map((o) => (
            <label className="fawkes-di-pil__hokje" key={o}>
              <input
                type="checkbox"
                checked={gekozen.includes(o)}
                onChange={(e) => onZet(e.target.checked
                  ? [...gekozen, o]
                  : gekozen.filter((x) => x !== o))}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </details>
    </div>
  );
}

export function DataInsightFilters({
  onderdelen,
  gebieden,
  vestigingen,
  keuze,
  overnemen = 0,
  onToepassen,
  bezig,
  metOnderdeel = true,
  t,
}: {
  onderdelen: Onderdeel[];
  /** De gemeenten met hun woonplaatsen; leeg laat alleen "alle gemeenten" staan. */
  gebieden: Gebied[];
  /** De vestigingen waar deze gebruiker bij mag; leeg verbergt de keuze. */
  vestigingen: string[];
  keuze: FilterKeuze;
  /**
   * Een teller die omhoog gaat zodra het scherm de stand met opzet zet.
   *
   * <p>Nodig om twee dingen te onderscheiden die er van hieruit hetzelfde uitzien: een gewone
   * hertekening van boven, en "neem deze stand over" bij het openen van een bewaarde analyse. Het
   * eerste mag niets wissen, het tweede moet alles zetten.
   */
  overnemen?: number;
  onToepassen: (k: FilterKeuze) => void;
  bezig: boolean;
  /**
   * Of de onderdeelkeuze erbij staat.
   *
   * <p>Op het AI-tabblad niet: daar bepaalt de vraag wat er geteld wordt, en een keuzelijst ernaast
   * zou suggereren dat die meedoet. De filters eronder gelden voor beide.
   */
  metOnderdeel?: boolean;
  t: (key: string) => string;
}) {
  // De balk houdt zijn eigen concept vast tot er op Filter wordt geklikt; pas dan gaat hij naar boven.
  const [concept, setConcept] = useState<FilterKeuze>(keuze);
  // Alleen overnemen zolang er nog geen onderdeel gekozen is — dus bij het eerste binnenkomen van
  // de lijst. Zou dit bij élke wijziging van boven overnemen, dan wist een antwoord dat net
  // binnenkomt stilletjes het jaartal dat iemand op dat moment intikt. Dat gebeurt zelden en is
  // precies daarom vervelend: je ziet niet dat het gebeurt, je ziet alleen het verkeerde cijfer.
  useEffect(() => {
    if (!concept.onderdeel && keuze.onderdeel) {
      setConcept(keuze);
    }
  }, [keuze, concept.onderdeel]);

  // En bij een bewuste stand van boven: alles overnemen, ook als er al iets gekozen was. De teller
  // is het signaal; de keuze zelf verandert ook bij een gewone hertekening.
  useEffect(() => {
    if (overnemen > 0) {
      setConcept(keuze);
    }
    // Met opzet alleen op de teller: hem laten reageren op "keuze" maakt hem weer even breed als
    // de hertekening waar hij zich juist van moet onderscheiden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overnemen]);

  const gekozen = onderdelen.find((o) => o.id === concept.onderdeel);
  const families = [...new Set(onderdelen.map((o) => o.familie))];
  const metPeriode = gekozen?.heeftPeriode ?? false;
  const snelkeuzes = periodeSnelkeuzes();
  // Welke snelkeuze exact overeenkomt met wat er staat. Zonder dat zou er altijd één opgelicht
  // blijven staan nadat iemand de datums met de hand heeft bijgesteld, en dan liegt de balk.
  const actieveSnelkeuze = snelkeuzes.find((s) =>
    s.stand.eenheid === concept.eenheid
    && (s.stand.vanDatum ?? null) === (concept.vanDatum ?? null)
    && (s.stand.totDatum ?? null) === (concept.totDatum ?? null)
    && (s.stand.vanJaar ?? null) === (concept.vanJaar ?? null)
    && (s.stand.totJaar ?? null) === (concept.totJaar ?? null))?.sleutel;

  const zet = (deel: Partial<FilterKeuze>) => setConcept({ ...concept, ...deel });

  // Terug naar de uitgangsstand, met behoud van het gekozen onderdeel: wie de filters wegdoet wil
  // de afbakening kwijt, niet het cijfer waar hij naar keek.
  const nu = new Date().getFullYear();
  const herstel = () => setConcept({
    onderdeel: concept.onderdeel, vanJaar: nu - 1, totJaar: nu, vanDatum: null, totDatum: null,
    diersoorten: [], uitsplitsing: "", gemeente: "", ouderDan: null, ouderDanEenheid: "MAANDEN",
    jongerDan: null, jongerDanEenheid: "MAANDEN", eenheid: "JAAR", cumulatief: false,
    vestigingen: [],
  });

  // Alle woonplaatsen in één alfabetische lijst, zonder dubbelen: dezelfde plaats kan in twee
  // gemeenten voorkomen, en twee identieke regels in een keuzelijst laten je zoeken naar het
  // verschil dat er niet is.
  const woonplaatsen = [...new Set(gebieden.flatMap((g) => g.woonplaatsen))]
    .filter((w) => w.trim() !== "")
    .sort((a, b) => a.localeCompare(b, "nl"));

  const diersoortTekst = concept.diersoorten.length === 0
    ? t("dataInsight.alleSoorten")
    : concept.diersoorten.length <= 2
      ? concept.diersoorten.join(", ")
      : concept.diersoorten.length + " " + t("dataInsight.gekozen");

  return (
    <form
      className="fawkes-di-filters"
      onSubmit={(e) => { e.preventDefault(); onToepassen(concept); }}
    >
      <div className="fawkes-di-filters__kop">
        <span className="fawkes-di-filters__kopicoon" aria-hidden="true"><ListFilter /></span>
        <div className="fawkes-di-filters__koptekst">
          <h2 className="fawkes-di-filters__koptitel">{t("dataInsight.filterKop")}</h2>
          <p className="fawkes-di-filters__kopsub">{t("dataInsight.filterSub")}</p>
        </div>
        <button type="button" className="fawkes-di-filters__herstel" onClick={herstel}>
          <RotateCcw aria-hidden="true" />
          {t("dataInsight.filtersHerstellen")}
        </button>
      </div>

      {/* De periode staat op zijn eigen regel en niet als één pil tussen de andere. Het zijn drie
          keuzes die bij elkaar horen — hoe fijn de as loopt, en van wanneer tot wanneer — en
          samengeperst in één pil werd het een rij vakjes zonder kop waarvan je moest gokken welk
          vakje wat deed. Onderdelen die over "nu" gaan hebben geen periode; de velden dan tonen
          maar negeren is erger dan ze weglaten, want dan vult iemand een datum in en verandert er
          niets. */}
      {metPeriode ? (
        <div className="fawkes-di-periode">
          <div className="fawkes-di-periode__regel">
            <div className="fawkes-di-filter">
              <span className="fawkes-di-filter__label">{t("dataInsight.eenheid")}</span>
              <div className="fawkes-di-pil fawkes-di-pil--tijd">
                <span className="fawkes-di-pil__icoon" aria-hidden="true"><CalendarRange /></span>
                <PilKeuze
                  waarde={concept.eenheid}
                  aria={t("dataInsight.eenheid")}
                  onKies={(v) => zet(bijEenheid(concept, v as Eenheid))}
                  kinderen={EENHEDEN.map((e) => (
                    <option key={e.sleutel} value={e.sleutel}>{t(e.label)}</option>
                  ))}
                />
              </div>
            </div>

            <div className="fawkes-di-filter">
              <span className="fawkes-di-filter__label">{t("dataInsight.periode")}</span>
              <div className="fawkes-di-pil fawkes-di-pil--tijd">
                <span className="fawkes-di-pil__icoon" aria-hidden="true"><CalendarDays /></span>
                {/* Per jaar is een dag betekenisloos: dan twee jaartallen. Fijner dan een jaar
                    heeft een jaartal juist te weinig houvast — "2025" zegt bij een as per week
                    niet waar je wil beginnen. */}
                {concept.eenheid === "JAAR" ? (
                  <span className="fawkes-di-pil__periode">
                    <input
                      type="number" className="fawkes-di-pil__jaar"
                      value={concept.vanJaar ?? ""} min={1980} max={2100}
                      aria-label={t("dataInsight.vanJaar")}
                      onChange={(e) => zet({
                        vanJaar: e.target.value ? Number(e.target.value) : null })}
                    />
                    <span className="fawkes-di-pil__tot">{t("dataInsight.tot")}</span>
                    <input
                      type="number" className="fawkes-di-pil__jaar"
                      value={concept.totJaar ?? ""} min={1980} max={2100}
                      aria-label={t("dataInsight.totJaar")}
                      onChange={(e) => zet({
                        totJaar: e.target.value ? Number(e.target.value) : null })}
                    />
                  </span>
                ) : (
                  <span className="fawkes-di-pil__periode">
                    <DateField className="fawkes-di-pil__datum"
                      value={concept.vanDatum ?? ""}
                      aria-label={t("dataInsight.vanDatum")}
                      onChange={(e) => zet({ vanDatum: e.target.value || null })}
                    />
                    <span className="fawkes-di-pil__tot">{t("dataInsight.tot")}</span>
                    <DateField className="fawkes-di-pil__datum"
                      value={concept.totDatum ?? ""}
                      aria-label={t("dataInsight.totDatum")}
                      onChange={(e) => zet({ totDatum: e.target.value || null })}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* De snelkeuzes zetten het bereik én de eenheid: "deze maand" met een as per jaar levert
              één staaf op, en vijf jaar per dag levert achttienhonderd staven. De combinatie is wat
              iemand bedoelt, niet het bereik alleen. */}
          <div className="fawkes-di-periode__snel" role="group"
            aria-label={t("dataInsight.periodeSnel")}>
            {snelkeuzes.map((s) => (
              <button
                key={s.sleutel}
                type="button"
                className={"fawkes-di-periode__keuze"
                  + (actieveSnelkeuze === s.sleutel ? " fawkes-di-periode__keuze--aan" : "")}
                aria-pressed={actieveSnelkeuze === s.sleutel}
                onClick={() => zet(s.stand)}
              >
                {t(s.label)}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="fawkes-di-filters__geenPeriode">{t("dataInsight.geenPeriode")}</p>
      )}

      <div className="fawkes-di-filters__pillen">
        {metOnderdeel ? (
        <Filter
          breed
          kleur="onderdeel"
          icoon={<Layers />}
          naam={t("dataInsight.onderdeel")}
          kind={
            <PilKeuze
              waarde={concept.onderdeel}
              aria={t("dataInsight.onderdeel")}
              onKies={(v) => zet({ onderdeel: v })}
              kinderen={
                /* Per familie gegroepeerd, net als in het oude scherm: "Intake:", "Verblijf:",
                   "Bezetting:". Een platte lijst van drieëndertig is niet te overzien. */
                families.map((f) => (
                  <optgroup key={f} label={f}>
                    {onderdelen.filter((o) => o.familie === f).map((o) => (
                      <option key={o.id} value={o.id}>{o.naam}</option>
                    ))}
                  </optgroup>
                ))
              }
            />
          }
        />
        ) : null}

        {/* "Uitsplitsen naar" en niet "Groep": dit is de as van de grafiek, en het stond verwarrend
            naast het gemeentefilter — twee keer "Gemeente", waarvan de een weglaat en de ander
            uitsplitst. */}
        <Filter
          kleur="as"
          icoon={<Split />}
          naam={t("dataInsight.uitsplitsen")}
          kind={
            <PilKeuze
              waarde={concept.uitsplitsing}
              aria={t("dataInsight.uitsplitsen")}
              onKies={(v) => zet({ uitsplitsing: v })}
              kinderen={GROEPEN.map((g) => (
                <option key={g.pad} value={g.pad}>{t(g.sleutel)}</option>
              ))}
            />
          }
        />

        {/* Alleen bij "Vestigingen": daar gaat de vraag over wélke, en pas dan is een lijst met
            aanvinkhokjes iets anders dan een lijst die er altijd staat te wachten. Niets
            aangevinkt betekent de vestiging waar je in zit — niet "alle", want wat je mag zien
            bepaalt de server. */}
        {concept.uitsplitsing === "animal.opnameVestiging" && vestigingen.length > 1 ? (
          <PilLijst
            kleur="vestiging"
            icoon={<Building2 />}
            naam={t("dataInsight.welkeVestigingen")}
            samenvatting={concept.vestigingen.length === 0
              ? t("dataInsight.huidigeVestiging")
              : concept.vestigingen.length + " " + t("dataInsight.gekozen")}
            opties={vestigingen}
            gekozen={concept.vestigingen}
            onZet={(v) => zet({ vestigingen: v })}
          />
        ) : null}

        <PilLijst
          kleur="soort"
          icoon={<PawPrint />}
          naam={t("dataInsight.diersoort")}
          samenvatting={diersoortTekst}
          opties={DIERSOORTEN}
          gekozen={concept.diersoorten}
          onZet={(v) => zet({ diersoorten: v })}
        />

        {/* Twee vrije grenzen en geen vaste groepen. De oude lijst begon bij "jonger dan 9 weken",
            en negen weken is een honden- en kattenmaat: voor een konijn of een vogel betekent die
            leeftijd iets heel anders. Wie de grens zelf zet heeft die lijst niet nodig. */}
        <Filter
          breed
          kleur="leeftijd"
          icoon={<CakeSlice />}
          naam={t("dataInsight.leeftijd")}
          kind={
            <span className="fawkes-di-pil__leeftijd">
              <span className="fawkes-di-pil__tot">{t("dataInsight.ouderDan")}</span>
              <input
                type="number" className="fawkes-di-pil__getal"
                value={concept.ouderDan ?? ""} min={0} max={400}
                aria-label={t("dataInsight.ouderDan")}
                onChange={(e) => zet({ ouderDan: e.target.value ? Number(e.target.value) : null })}
              />
              <PilKeuze
                waarde={concept.ouderDanEenheid}
                aria={t("dataInsight.ouderDanEenheid")}
                onKies={(v) => zet({ ouderDanEenheid: v as Leeftijdseenheid })}
                kinderen={LEEFTIJDSEENHEDEN.map((e) => (
                  <option key={e.sleutel} value={e.sleutel}>{t(e.label)}</option>
                ))}
              />
              <span className="fawkes-di-pil__scheiding" aria-hidden="true" />
              <span className="fawkes-di-pil__tot">{t("dataInsight.jongerDan")}</span>
              <input
                type="number" className="fawkes-di-pil__getal"
                value={concept.jongerDan ?? ""} min={0} max={400}
                aria-label={t("dataInsight.jongerDan")}
                onChange={(e) => zet({ jongerDan: e.target.value ? Number(e.target.value) : null })}
              />
              <PilKeuze
                waarde={concept.jongerDanEenheid}
                aria={t("dataInsight.jongerDanEenheid")}
                onKies={(v) => zet({ jongerDanEenheid: v as Leeftijdseenheid })}
                kinderen={LEEFTIJDSEENHEDEN.map((e) => (
                  <option key={e.sleutel} value={e.sleutel}>{t(e.label)}</option>
                ))}
              />
            </span>
          }
        />

        {/* Gemeenten en woonplaatsen in één keuzelijst, in twee groepen. De woonplaatsen krijgen
            een voorvoegsel mee: een woonplaats kan dezelfde naam hebben als een gemeente
            ("Utrecht"), en zonder dat onderscheid zou de keuze op de verkeerde kolom filteren en
            stilletjes een ander aantal opleveren. */}
        <Filter
          kleur="gebied"
          icoon={<MapPin />}
          naam={t("dataInsight.gebied")}
          kind={
            <PilKeuze
              waarde={concept.gemeente}
              aria={t("dataInsight.gebied")}
              onKies={(v) => zet({ gemeente: v })}
              kinderen={[
                <option key="" value="">{t("dataInsight.alleGemeenten")}</option>,
                <optgroup key="g" label={t("dataInsight.gemeenten")}>
                  {gebieden.map((g) => (
                    <option key={g.naam} value={g.naam}>{g.naam}</option>
                  ))}
                </optgroup>,
                <optgroup key="w" label={t("dataInsight.woonplaatsen")}>
                  {woonplaatsen.map((w) => (
                    <option key={w} value={WOONPLAATS_VOORVOEGSEL + w}>{w}</option>
                  ))}
                </optgroup>,
              ]}
            />
          }
        />

        <button type="submit" className="fui-button fui-button--primary" disabled={bezig}>
          {bezig ? t("dataInsight.bezig") : t("dataInsight.filter")}
        </button>
      </div>

      {/* Dieren zonder geboortedatum hebben geen leeftijd en vallen dus buiten elke grens. Dat is
          onvermijdelijk maar niet onschuldig: zonder deze regel verdwijnen ze zonder dat er iets
          over gezegd wordt. */}
      {concept.ouderDan || concept.jongerDan ? (
        <p className="fawkes-di-filters__waarschuwing">{t("dataInsight.leeftijdOnbekend")}</p>
      ) : null}

      {/* De definitie staat onder de balk en niet in een uitklapje: wat er geteld wordt hoort te
          worden gelezen vóór het cijfer wordt overgenomen, niet erna. */}
      {gekozen ? (
        <p className="fawkes-di-filters__uitleg">{gekozen.uitleg}</p>
      ) : null}
    </form>
  );
}
